import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bookmark, BookmarkCheck, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { setAfterAuth } from "@/components/CouponOfDay";
import { loadSavedIds, onSavedChange, toggleSaved, rememberSignupCta } from "@/lib/membership";

// "Salva" su ogni articolo: riservato agli iscritti, per gli altri è un invito a registrarsi.
export function SaveButton({ articleId, testid = "save-article-button" }) {
  const { user } = useAuth();
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [ask, setAsk] = useState(false);

  useEffect(() => {
    if (!user) return undefined;
    loadSavedIds().then((ids) => setSaved(ids.has(articleId))).catch(() => {});
    return onSavedChange((ids) => setSaved(!!ids?.has(articleId)));
  }, [user, articleId]);

  const click = async () => {
    if (!user) { setAsk(true); return; }
    setBusy(true);
    try {
      const on = await toggleSaved(articleId);
      toast.success(on ? "Salvato in «Salvati per dopo»" : "Tolto dai salvati");
    } catch (e) { toast.error(e.message || "Operazione non riuscita"); } finally { setBusy(false); }
  };

  return (
    <>
      <button type="button" onClick={click} disabled={busy} data-testid={testid} aria-pressed={saved}
        aria-label={saved ? "Togli dai salvati" : "Salva per dopo"}
        className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border text-sm font-semibold transition-colors disabled:opacity-60 ${saved ? "border-brand bg-brand/10 text-brand" : "border-hair text-onSurfaceSecondary hover:border-brand/50 hover:text-brand"}`}>
        {saved ? <BookmarkCheck size={16} /> : <Bookmark size={16} />} {saved ? "Salvato" : "Salva"}
      </button>
      {ask && <SaveInvite onClose={() => setAsk(false)} />}
    </>
  );
}

function SaveInvite({ onClose }) {
  const go = () => { rememberSignupCta("salva"); setAfterAuth(window.location.pathname); };
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/60" onClick={onClose} data-testid="save-invite-modal">
      <div className="w-full max-w-md bg-surfaceSecondary border border-hair rounded-2xl p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <span className="w-11 h-11 rounded-full bg-brand/15 text-brand flex items-center justify-center"><Bookmark size={20} /></span>
          <button type="button" onClick={onClose} data-testid="save-invite-close" aria-label="Chiudi" className="w-8 h-8 flex items-center justify-center rounded-full border border-hair text-subtle hover:text-brand"><X size={16} /></button>
        </div>
        <p data-testid="save-invite-text" className="mt-4 font-serif text-xl font-semibold leading-snug text-onSurface">
          Registrati gratuitamente per salvare questa notizia e ritrovarla quando vuoi.
        </p>
        <Link to="/registrati" onClick={go} data-testid="save-invite-register"
          className="mt-5 w-full inline-flex items-center justify-center px-6 py-3.5 rounded-full bg-brand text-onBrand text-sm font-bold uppercase tracking-wide hover:bg-brandSecondary transition-colors">
          Registrati gratis
        </Link>
        <p className="mt-3 text-center text-xs text-subtle">Hai già un account? <Link to="/login" onClick={go} data-testid="save-invite-login" className="text-brand font-semibold hover:underline">Accedi</Link></p>
      </div>
    </div>
  );
}
